import { atom } from "jotai";
import type { Model, ModelTemplate } from "@/modules/plus/plus.schema";
import type { AtomContainer } from "@/state";

interface ListedModel extends Model {
  hasTemplate: boolean;
}

export interface SheetSelection {
  modelCode: string;
  template: ModelTemplate | null;
  model: Model | null;
}

export class ModelsAtoms {
  constructor(private readonly atoms: AtomContainer) {}

  // The codes that resolve to a template — series_ids plus every key under
  // each template's first (Model) field.
  public codesWithTemplate = atom<ReadonlySet<string>>((get) => {
    const templates = get(this.atoms.data.templates);
    return new Set(templates.flatMap((g) => Object.keys(g.fields[0]?.codes ?? {})));
  });

  // All models from the canonical Firestore collection, sorted by code,
  // annotated with template availability.
  public listed = atom<ListedModel[]>((get) => {
    const models = get(this.atoms.data.models);
    const withTemplate = get(this.codesWithTemplate);
    return Object.values(models)
      .map((m) => ({ ...m, hasTemplate: withTemplate.has(m.model) }))
      .sort((a, b) => a.model.localeCompare(b.model));
  });

  public filtered = atom<ListedModel[]>((get) => {
    const q = get(this.atoms.search.text).trim().toLowerCase();
    const all = get(this.listed);
    if (!q) {
      return all;
    }
    return all.filter(
      (m) =>
        m.model.toLowerCase().includes(q) ||
        m.series.toLowerCase().includes(q) ||
        m.description.toLowerCase().includes(q),
    );
  });

  // Currently-open sheet. Basic rows still open the sheet — they just render
  // DB info without the ValuePicker.
  public sheetSelected = atom<SheetSelection | null>(null);

  public openSheet = atom(null, (get, set, modelCode: string) => {
    const templates = get(this.atoms.data.templates);
    const models = get(this.atoms.data.models);
    const template =
      templates.find(
        (g) => g.series_id === modelCode || Object.hasOwn(g.fields[0]?.codes ?? {}, modelCode),
      ) ?? null;
    const model = models[modelCode] ?? null;
    if (!template && !model) {
      return;
    }
    set(this.sheetSelected, { modelCode, template, model });
  });

  public closeSheet = atom(null, (_get, set) => {
    set(this.sheetSelected, null);
  });
}
